/**
 * Aeronaves: o estado de cada aeronave numa frase e UMA lista sem repetições.
 *
 * Tudo vem do que o servidor já calcula:
 *   - aircraft.operational_state  (bloqueios e avisos: a palavra final sobre "pode voar")
 *   - alerts                      (GET /admin/aircraft-alerts-live: os mesmos alertas do sino)
 *   - insurance, gapInfo          (apólice atual e registos em falta da caderneta)
 *
 * Regras que garantem que nada se perde nem se repete:
 *   1. Os bloqueios do estado operacional aparecem SEMPRE (são a lista completa do que não deixa voar).
 *   2. Um alerta que já está contado num bloqueio (o texto do bloqueio contém o nome do alerta) não se repete.
 *   3. Um aviso do estado operacional sem alerta correspondente (ex.: "Sem motor registado") aparece na mesma.
 *   4. Uma inspeção por horas que outra maior já cobre (a de 50 h cobre a de 25 h, se vencer antes ou ao mesmo
 *      tempo) não se repete: o servidor diz quais cada alerta cobre (`covers`) e a lista segue a mesma regra do sino.
 */

const STATUS_BLOCK = /^Aeronave (marcada como retirada|em manutenção)\.?$/;
const MAINT_BLOCK = /^Manutenção '(.+?)'(?: fora de tolerância)?: (.+?)\.?$/;
const MAINT_WARNING = /^Manutenção '(.+?)'( vencida, dentro da tolerância| em extensão do potencial)?: (.+?)\.?$/;
const DOC_EXPIRED = /^'(.+?)' expirou há (\d+) dias?\.?$/;
const DOC_MISSING = /^Sem registo de '(.+?)'\.?$/;

const SEV_RANK = { critical: 0, warn: 1, info: 2 };

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const lc = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export function fmtDatePt(iso) {
  if (!iso || iso.length < 10) return "";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}

function stripLabel(message, label) {
  let m = message || "";
  if (label) {
    for (const sep of [": ", " "]) {
      if (m.startsWith(label + sep)) { m = m.slice(label.length + sep.length); break; }
    }
  }
  return m.replace(/\.$/, "");
}

function targetOfKind(kind) {
  const k = kind || "";
  if (k.startsWith("insurance")) return "insurance";
  return k.startsWith("maintenance") || k === "aircraft_maintenance" ? "maintenance" : "documents";
}

/** Um alerta "bloqueia" quando é um documento vencido/em falta ou uma manutenção fora de tolerância (crítica). */
function isBlockingAlert(al) {
  if (al.kind === "aircraft_document_missing") return true;
  if (al.kind === "aircraft_document") return al.days_remaining != null && al.days_remaining < 0;
  return targetOfKind(al.kind) === "maintenance" && al.severity === "critical";
}

function alertWhen(al) {
  if (al.kind === "aircraft_document") {
    const d = al.days_remaining;
    const date = al.valid_until ? ` (${fmtDatePt(al.valid_until)})` : "";
    if (d == null) return cap(stripLabel(al.message, al.label));
    if (d < 0) return `Expirou há ${-d} ${-d === 1 ? "dia" : "dias"}${date}`;
    if (d === 0) return `Expira hoje${date}`;
    return `Expira em ${d} ${d === 1 ? "dia" : "dias"}${date}`;
  }
  if (al.kind === "aircraft_document_missing") return "Sem data de validade registada";
  if (al.kind === "insurance_policy") {
    const d = al.days_remaining;
    const date = al.valid_until ? ` (${fmtDatePt(al.valid_until)})` : "";
    if (d == null) return cap(stripLabel(al.message, al.label));
    if (d < 0) return `Expirou há ${-d} ${-d === 1 ? "dia" : "dias"}${date}`;
    if (d === 0) return `Termina hoje${date}`;
    return `Termina em ${d} ${d === 1 ? "dia" : "dias"}${date}`;
  }
  if (al.kind === "insurance_payment_overdue") {
    const d = al.days_remaining;
    const amount = /\((€[\d.,]+)\)/.exec(al.message || "")?.[1];
    if (d != null && d < 0) return `Atrasada há ${-d} ${-d === 1 ? "dia" : "dias"}${amount ? ` (${amount})` : ""}`;
  }
  if (al.short) return cap(al.short);
  const text = cap(stripLabel(al.message, al.label));
  const date = al.valid_until && al.days_remaining != null && al.days_remaining >= 0 ? ` (${fmtDatePt(al.valid_until)})` : "";
  return text + date;
}

function blockRow(text, alerts) {
  const matched = alerts.find((al) => al.label && text.includes(al.label));
  let title = text;
  let when = "";
  let target = "documents";
  let m;
  if ((m = MAINT_BLOCK.exec(text))) {
    title = m[1]; when = cap(m[2]); target = "maintenance";
  } else if ((m = DOC_EXPIRED.exec(text))) {
    title = m[1]; when = `Expirou há ${m[2]} ${m[2] === "1" ? "dia" : "dias"}`;
    if (matched?.valid_until) when += ` (${fmtDatePt(matched.valid_until)})`;
  } else if ((m = DOC_MISSING.exec(text))) {
    title = m[1]; when = "Sem data de validade registada";
  }
  return { key: `block:${text}`, group: "block", severity: "critical", title, when, target };
}

/** Aviso do estado operacional que não tem alerta (ex.: tarefa "dentro" de uma inspeção maior, ou "Sem motor registado"). */
function warningRow(text) {
  const m = MAINT_WARNING.exec(text);
  if (m) {
    const mid = m[2] ? `${m[2].trim()}: ` : "";
    return { key: `aviso:${text}`, group: "attention", severity: "warn", title: m[1], when: cap(mid + lc(m[3])), target: "maintenance", days: null, overdue: /vencida/i.test(mid) };
  }
  const head = text.split(/[:.]/)[0];
  return { key: `aviso:${text}`, group: "attention", severity: "warn", title: cap(head), when: cap(text.slice(head.length).replace(/^[:.]\s*/, "")), target: "maintenance", days: null };
}

function alertRow(al, i) {
  return {
    key: `alert:${al.kind}:${al.label || al.target}:${i}`,
    group: al.severity === "info" ? "later" : "attention",
    severity: al.severity || "info",
    title: al.label || al.target || "Aviso",
    when: alertWhen(al),
    target: targetOfKind(al.kind),
    days: al.days_remaining,
    minutes: al.minutes_remaining,
  };
}

function insuranceRow(insurance, hasPolicyAlert) {
  // Se o servidor já mandou o alerta da apólice, a linha vem dele (e não se repete); esta serve o "sem seguro"
  // e as apólices a acabar entre 45 e 90 dias, que ainda não têm alerta.
  if (insurance && hasPolicyAlert) return null;
  if (!insurance) {
    return { key: "insurance:none", group: "attention", severity: "critical", title: "Seguro", when: "Sem seguro registado", target: "insurance" };
  }
  const days = daysUntil(insurance.policy_end);
  if (days == null || days >= 90) return null;
  const title = insurance.insurer ? `Seguro (${insurance.insurer})` : "Seguro";
  const date = ` (${fmtDatePt(insurance.policy_end)})`;
  if (days < 0) return { key: "insurance", group: "attention", severity: "critical", title, when: `Expirou há ${-days} ${-days === 1 ? "dia" : "dias"}${date}`, target: "insurance", days };
  const when = days === 0 ? `Termina hoje${date}` : `Termina em ${days} ${days === 1 ? "dia" : "dias"}${date}`;
  return { key: "insurance", group: days < 45 ? "attention" : "later", severity: days < 45 ? "warn" : "info", title, when, target: "insurance", days };
}

function gapRow(gapInfo) {
  const total = (gapInfo?.service_numbers?.missing_count || 0) + (gapInfo?.navigation_log_numbers?.missing_count || 0);
  if (total <= 0) return null;
  return { key: "logbook-gaps", group: "later", severity: "info", title: "Caderneta com registos em falta", when: `${total} ${total === 1 ? "número" : "números"} por preencher`, target: "logbook" };
}

/**
 * Ordem ÚNICA dos avisos (o servidor usa a mesma regra no sino: services/di_aircraft.py::alert_order_key):
 * 1. gravidade (crítico, atenção, informação);
 * 2. dentro da mesma gravidade, primeiro o que JÁ passou o prazo (calendário, depois horas; o mais atrasado
 *    primeiro), depois o que ainda vem (por dias, depois por horas), por fim o que não tem prazo;
 * 3. desempate pelo nome.
 */
function orderParts(r) {
  const d = r.days;
  const m = r.minutes;
  if (d != null && d < 0) return [0, d];
  if (d == null && ((m != null && m < 0) || r.overdue)) return [1, m ?? 0];
  if (d != null) return [2, d];
  if (m != null) return [3, m];
  return [4, 0];
}

export function compareUrgency(a, b) {
  const s = (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9);
  if (s) return s;
  const [ga, wa] = orderParts(a);
  const [gb, wb] = orderParts(b);
  if (ga !== gb) return ga - gb;
  if (wa !== wb) return wa - wb;
  return String(a.title || "").localeCompare(String(b.title || ""));
}

/** Estado de uma aeronave: { kind, phrase, reason, blocks, attention, later, attentionCount } */
export function buildAircraftView({ aircraft, insurance, gapInfo, alerts }) {
  const al = alerts || [];
  const state = aircraft?.operational_state || {};
  const status = aircraft?.status || "ativa";
  const bloqueios = (state.bloqueios || []).filter((b) => !STATUS_BLOCK.test(b));
  const avisos = state.avisos || [];

  // Tarefas que um alerta maior já cobre (o servidor decide: ver `covers`): não se repetem na lista
  const coveredNames = al.flatMap((a) => a.covers || []).filter(Boolean);
  const isCovered = (text) => coveredNames.some((name) => text.includes(name));

  const blocks = bloqueios.filter((b) => !isCovered(b)).map((b) => blockRow(b, al));

  const rows = [];
  al.forEach((a, i) => {
    if (isBlockingAlert(a) && a.label && bloqueios.some((b) => b.includes(a.label))) return;
    rows.push(alertRow(a, i));
  });
  avisos.forEach((w) => {
    if (isCovered(w)) return;
    if (al.some((a) => a.label && w.includes(a.label))) return;
    rows.push(warningRow(w));
  });
  const ins = insuranceRow(insurance, al.some((a) => a.kind === "insurance_policy"));
  if (ins) rows.push(ins);
  const gap = gapRow(gapInfo);
  if (gap) rows.push(gap);

  const attention = rows.filter((r) => r.group === "attention").sort(compareUrgency);
  const later = rows.filter((r) => r.group === "later").sort(compareUrgency);

  const describe = (r) => (r.when ? `${r.title}: ${lc(r.when)}` : r.title);
  let kind;
  let phrase;
  let reason;
  if (status === "retirada") {
    kind = "retired"; phrase = "Retirada de serviço"; reason = "Fora de serviço";
  } else if (status === "manutencao") {
    kind = "grounded"; phrase = "Em manutenção"; reason = "Marcada como em manutenção";
  } else if (blocks.length > 0 || state.operacional === false) {
    kind = "grounded"; phrase = "Não pode voar";
    reason = blocks.length ? describe(blocks[0]) + (blocks.length > 1 ? ` (+${blocks.length - 1})` : "") : "Ver detalhes";
  } else if (attention.length > 0) {
    kind = "warn";
    phrase = `Pode voar, com ${attention.length} ${attention.length === 1 ? "aviso" : "avisos"}`;
    reason = describe(attention[0]) + (attention.length > 1 ? ` (+${attention.length - 1})` : "");
  } else {
    kind = "ok"; phrase = "Pode voar";
    reason = later.length ? `A seguir: ${describe(later[0])}` : "Tudo em dia";
  }
  return { kind, phrase, reason, blocks, attention, later, attentionCount: attention.length, covered: coveredNames };
}

const KIND_RANK = { grounded: 0, warn: 1, ok: 2, retired: 3 };

export function sortByUrgency(a, b) {
  const k = (KIND_RANK[a.view.kind] ?? 9) - (KIND_RANK[b.view.kind] ?? 9);
  if (k) return k;
  return (a.aircraft.registration || "").localeCompare(b.aircraft.registration || "");
}

export function summarizeFleet(views) {
  const counts = { grounded: 0, warn: 0, ok: 0, retired: 0 };
  views.forEach((v) => { counts[v.kind] = (counts[v.kind] || 0) + 1; });
  return counts;
}
